import React, { useEffect, useState } from "react";
import { Plus, TrendingUp } from "lucide-react";
import { toast } from "../ui/Toast";

// Manager view: nominate a report for a career event (promotion / grade / title
// / transfer) and see their own nominations. Managers CANNOT approve/reject
// (admin-only) — no such controls here. current_* are server-snapshotted at
// create, so the form never collects them.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  pending_hr: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  approved:   "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  rejected:   "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { pending_hr: "Pending HR", approved: "Approved", rejected: "Rejected" };
const EVENT_OPTS = [["promotion", "Promotion"], ["grade_change", "Grade Change"], ["title_change", "Title Change"], ["transfer", "Transfer"]];
const EVENT_LABEL = Object.fromEntries(EVENT_OPTS);

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const statusBadge = (s) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || STATUS_FALLBACK}`}>{STATUS_LABEL[s] || s || "—"}</span>
);

const EMPTY_FORM = {
  employee_id: "", event_type: "promotion", proposed_title: "", proposed_grade: "",
  proposed_salary: "", effective_date: "", justification: "",
};

export default function ManagerCareerView() {
  const [events, setEvents]       = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, "Content-Type": "application/json" };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/career-events/team`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      setEvents(Array.isArray(data) ? data : []);
    } catch { setEvents([]); }
    finally { setLoading(false); }
  };

  const loadEmployees = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/performance/team`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      const arr = Array.isArray(data) ? data : (data?.data || []);
      setEmployees(arr.map(r => ({ id: r.user_id, name: r.name || r.user_id })).filter(e => e.id));
    } catch { setEmployees([]); }
  };

  useEffect(() => { loadEvents(); loadEmployees(); }, []); // eslint-disable-line

  const submitCreate = async () => {
    if (!form.employee_id || !form.justification.trim()) {
      toast.error("Select an employee and enter a justification");
      return;
    }
    setSaving(true);
    try {
      const body = {
        employee_id: form.employee_id,
        event_type: form.event_type,
        proposed_title: form.proposed_title || null,
        proposed_grade: form.proposed_grade || null,
        proposed_salary: form.proposed_salary === "" ? null : Number(form.proposed_salary),
        effective_date: form.effective_date || null,
        justification: form.justification,
      };
      const res = await fetch(`${API_BASE}/api/performance/career-events`, {
        method: "POST", headers: jsonH, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Career event submitted — awaiting HR decision");
      setForm(EMPTY_FORM); setShowCreate(false);
      await loadEvents();
    } catch { toast.error("Failed to submit career event"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-[#00ff90]" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Career Events</span>
        </div>
        <button onClick={() => setShowCreate(s => !s)}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
          <Plus size={12} /> Nominate
        </button>
      </div>

      {showCreate && (
        <div className={`${card} p-5 space-y-4`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Employee</label>
              <select className={inputCls} value={form.employee_id}
                onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}>
                <option value="">Select a report…</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Event Type</label>
              <select className={inputCls} value={form.event_type}
                onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
                {EVENT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Proposed Title</label>
              <input className={inputCls} value={form.proposed_title}
                onChange={e => setForm(f => ({ ...f, proposed_title: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Proposed Grade</label>
              <input className={inputCls} value={form.proposed_grade}
                onChange={e => setForm(f => ({ ...f, proposed_grade: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Proposed Salary</label>
              <input type="number" className={inputCls} value={form.proposed_salary}
                onChange={e => setForm(f => ({ ...f, proposed_salary: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Effective Date</label>
              <input type="date" className={inputCls} value={form.effective_date}
                onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Justification <span className="text-amber-400/60">*</span></label>
              <textarea className={inputCls} rows={2} value={form.justification}
                onChange={e => setForm(f => ({ ...f, justification: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1 border-t border-white/5">
            <button onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={submitCreate} disabled={saving || !form.employee_id || !form.justification.trim()}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      )}

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">My Nominations</span>
        </div>
        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>)
          ) : events.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No career events submitted.</p>
          ) : events.map(e => (
            <div key={e.id} className="flex items-start justify-between gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm text-white/80 font-medium">{e.employee_name || e.employee_id}</p>
                  <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">{EVENT_LABEL[e.event_type] || e.event_type}</span>
                  {statusBadge(e.status)}
                </div>
                <p className="text-xs text-white/40">
                  Title: {val(e.current_title)} → {val(e.proposed_title)} · Grade: {val(e.current_grade)} → {val(e.proposed_grade)}
                </p>
                <p className="text-xs text-white/25 mt-0.5">Effective: {fmtDate(e.effective_date)}</p>
              </div>
              <div className="shrink-0">
                {e.status === "pending_hr" && (
                  <span className="text-xs px-3 py-1.5 rounded-lg border border-amber-500/20 bg-amber-500/5 text-amber-400/70">Awaiting HR decision</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
